import type { LoginResponse, Principal, ODataList, Document, MetaEntity, MetaComplexType, MetaEnum, MetaEntitySummary, ScreenDef, ScreenSummary, AppUserRow, AppRoleRow, KpiRow } from '@b1links/core';
import { apiBase, detectChannel } from '../platform';
import { useAuth } from '../store/auth';

export class ApiError extends Error { constructor(public status: number, message: string, public sapCode?: string) { super(message); } }

async function http<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuth.getState().token;
  const res = await fetch(`${apiBase()}/api${path}`, {
    method, headers: { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401 && token) useAuth.getState().logout();
  const data = res.status === 204 ? undefined : await res.json().catch(() => ({}));
  if (!res.ok) {
    // Fastify's default error envelope puts the useful text in `message` and a generic reason phrase in
    // `error` (e.g. "Bad Request"); the app's own handlers put it in `error`. Prefer whichever is the detail.
    const detail = (data && data.statusCode !== undefined && data.message) ? data.message : (data?.error ?? data?.message ?? res.statusText);
    throw new ApiError(res.status, detail, data?.sapCode ?? data?.code);
  }
  return data as T;
}

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '' && v !== null && v !== false) p.set(k, String(v));
  const s = p.toString(); return s ? `?${s}` : '';
};

/** Full entity metadata as returned by /meta/entities/:set (with resolved complex types + enums). */
export type EntityMeta = MetaEntity & { complexTypes: Record<string, MetaComplexType>; enums: Record<string, MetaEnum> };

export interface ListParams { q?: string; filter?: string; select?: string; orderby?: string; expand?: string; top?: number; skip?: number; count?: boolean; searchFields?: string }

export type Rec = Record<string, unknown>;

export interface AttachmentFile { line: number; name: string; extension: string; date?: string; isImage: boolean }
export interface AttachmentList { supported: boolean; entry: number | null; files: AttachmentFile[] }
export interface Kpi { id: string; label: string; icon: string; tone: 'info'|'good'|'warn'|'neutral'; screen?: string; value: number; stale: boolean; asOf: string }
export interface MetaInfo { version: string; loadedAt: string; entities: number; udts: number; udos: number; udfFields: number; complexTypes: number; enums: number }
export interface OpenInvoice { DocEntry: number; DocNum: number; DocDate?: string; DocDueDate?: string; DocTotal: number; PaidToDate: number; DocCurrency?: string; OpenBalance: number }
export interface PermissionCatalog { wildcard: string; fixed: { id: string; label: string }[]; entities: { entity: string; label: string; view: string; edit: string }[] }
export interface Tenant { db: string; name: string; active: boolean; isControl: boolean; provisionedAt?: string; provisionedBy?: string }
export interface DiscoveredCompany { db: string; name: string; registered: boolean }
export interface ProvisionSummary { screens: { created: number; skipped: number; total: number }; kpis: { created: number; skipped: number; total: number }; adminCreated: boolean; log: string[] }
export interface Pack { id: string; name: string; description: string; category: 'Core' | 'Industry'; groups: string[]; defaultOn: boolean; locked?: boolean; enabled: boolean; screenCount: number }
export interface Coverage { companyDb: string; metadataLoadedAt?: string; entities: number; screens: number; covered: number; missing: { set: string; title: string; group: string; subgroup?: string }[]; excluded: { set: string; reason: string }[] }
export type AccessLevel = 'view' | 'edit';
export type AccessKind = 'user' | 'role' | 'sapuser';
export interface AccessGrantRow { kind: AccessKind; code: string; grants: Record<string, AccessLevel>; updatedAt?: string; updatedBy?: string }
export interface ScreenAccessData {
  companyDb: string; sapChannels: string[];
  screens: { id: string; title: string; entity: string; icon?: string; group: string; subgroup?: string; channels: string[]; packOff: boolean }[];
  users: { code: string; name: string; role?: string; active: boolean }[];
  roles: { code: string; name: string; superUser: boolean }[];
  sapUsers: { code: string; name: string; superUser: boolean }[]; sapUsersError?: string;
  grants: AccessGrantRow[];
}
export interface PostingField { key: string; label: string; group: 'Inventory' | 'Accounts' | 'Co-product prices' | 'Budgeting'; default: string; kind: 'text' | 'number'; value: string; effective: string }

/**
 * Portal identity. The same build serves several IIS applications; only the server's B1LINKS_PORTAL and base
 * path differ. `groups` empty means the full workspace.
 */
export interface PortalInfo { id: string; name: string; tagline: string; groups: string[] }

// ---- financial consolidation ----
export type ConsMethod = 'full' | 'proportionate' | 'equity';
export type ConsAccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export interface ConsGroup { id: string; name: string; currency: string; ctaAccount: string; note?: string; createdAt?: string; createdBy?: string }
export interface ConsMember {
  memberId: string;
  /** 'company' = a SAP company DB; 'group' = another group, nested without limit. */
  kind: 'company' | 'group';
  /** Share held by this member's immediate parent; effective ownership compounds down the tree. */
  ownershipPct: number;
  method: ConsMethod;
  currency?: string;
  seq?: number;
}
export interface ConsAccount { code: string; name: string; type: ConsAccountType; seq?: number }
export interface ConsTreeNode extends ConsMember { path: string[]; directPct: number; effectivePct: number; equityCarried: boolean }
export interface ConsGroupDetail {
  group: ConsGroup;
  members: ConsMember[];
  accounts: ConsAccount[];
  tree: ConsTreeNode[];
  warnings: string[];
}
export interface ConsSourceAccount {
  code: string; name: string; accountType: string;
  mapped: string; suggested: string;
  /** true when SAP's own AccountType decided it; false means the suggestion is a guess from the code prefix. */
  confident: boolean;
  hasActivity: boolean; net: number;
}
export interface ConsSourceAccounts {
  company: string;
  accounts: ConsSourceAccount[];
  counts: { total: number; withActivity: number; mapped: number; unmappedWithActivity: number };
}
export interface ConsJournalLine { line?: number; groupAccount: string; memberId?: string; amount: number; memo?: string }
export interface ConsJournal {
  id: string; year: string; period?: number;
  kind: 'elimination' | 'adjustment' | 'reclass';
  memo?: string; createdAt?: string; createdBy?: string;
  lines: ConsJournalLine[];
}
export interface ConsSyncResult {
  group: string;
  companies: { company: string; scanned: number; folded: number; lastJdt: number; more: boolean; error?: string }[];
  more: boolean;
}
export interface ConsTrialBalanceRow {
  code: string; name: string; type: ConsAccountType | 'unclassified';
  byMember: Record<string, number>; group: number; nci: number;
}
export interface ConsTrialBalance {
  group: { id: string; name: string; currency: string };
  year: string; fromPeriod: number; toPeriod: number;
  window: { from: string; to: string };
  members: Record<string, unknown>[];
  rows: ConsTrialBalanceRow[];
  totals: { group: number; nci: number; journalNet: number };
  unmapped: { member: string; account: string; amount: number }[];
  warnings: string[];
}

/** Budget vs actual, as served by GET /budget/variance. Actuals come from the incrementally-synced cache. */
export interface BudgetVarianceRow {
  key: string; label: string;
  budget: number; actual: number; variance: number;
  variancePct: number | null;
  /** False when the row carries no budget line at all — its spend is reported, never flagged as a breach. */
  budgeted: boolean;
  /**
   * Which direction this row is read in. 'mixed' only occurs in a non-account cut, where one cost centre,
   * project or partner aggregates both revenue and expense accounts.
   */
  kind: 'revenue' | 'expense' | 'mixed';
  /** Planned spend against spend. A positive variance is underspend. */
  expense: { budget: number; actual: number };
  /** Target against booked. Booking MORE than the target is good news, not a breach. */
  revenue: { budget: number; actual: number };
  /** Spent more than planned. Judged on the expense component only. */
  overBudget: boolean;
  /** Booked less than the revenue target promised — the revenue mirror of overBudget. */
  underTarget: boolean;
}
export interface BudgetVariance {
  version: { code: string; name?: string; year: string; status?: string; currency?: string };
  groupBy: 'account' | 'costCentre' | 'costCentre2' | 'project' | 'cardCode';
  fromPeriod: number; toPeriod: number;
  rows: BudgetVarianceRow[];
  /**
   * `budget`/`actual`/`variance` are the arithmetic total across both kinds. Never present that as a single
   * "% of budget": it adds a sales target to a cost ceiling. Use `expense` and `revenue`, which are measured
   * in opposite directions.
   */
  totals: {
    budget: number; actual: number; variance: number;
    expense: { budget: number; actual: number; variance: number };
    revenue: { budget: number; actual: number; variance: number };
  };
  /**
   * Spend on rows carrying no budget at all. Disjoint from `totals.actual` — the two add up to every
   * posting in the period range.
   */
  unbudgeted: { actual: number; buckets: number };
  /**
   * Spend INSIDE `totals.actual` that no budget line governs: it landed on an account that carries a
   * budget but matched no line, usually because the line pins a dimension the posting does not carry.
   * A SUBSET of the actual — never add it to anything.
   */
  unmatched: { actual: number; buckets: number };
  /** Structural problems that would otherwise fail silently, chiefly lines that can never match. */
  warnings: string[];
  /** When the journal scan last ran; null when actuals have never been synced for this company. */
  actualsAsOf: string | null;
  actualsLastJdt: number;
  note?: string;
}

export const api = {
  login: (userName: string, password: string, companyDb?: string) => http<LoginResponse>('POST', '/auth/login', { userName, password, companyDb, channel: detectChannel() }),
  logout: () => http<void>('POST', '/auth/logout'),
  me: () => http<Principal>('GET', '/auth/me'),
  /** Company DBs selectable at login (public). */
  companies: () => http<{ companies: string[]; default: string }>('GET', '/auth/companies'),
  /** Which portal this deployment is — B1Links, B1Budget or B1Conso. Public: the login page shows its name. */
  portal: () => http<PortalInfo>('GET', '/auth/portal'),

  // metadata + screens
  kpis: () => http<Kpi[]>('GET', '/kpi'),
  metaInfo: () => http<MetaInfo>('GET', '/meta'),
  refreshMeta: () => http<{ loadedAt: string; entities: number }>('POST', '/meta/refresh'),
  screenCoverage: () => http<Coverage>('GET', '/screens/coverage'),
  generateScreens: (sets?: string[]) => http<{ created: { id: string; entity: string; group: string }[]; failed: { entity: string; error: string }[] }>('POST', '/screens/generate', { sets }),
  seedScreens: (force = false) => http<{ seeded: string[] }>('POST', `/screens/seed${force ? '?force=true' : ''}`),
  metaEntities: (q?: { q?: string; category?: string }) => http<MetaEntitySummary[]>('GET', `/meta/entities${qs((q ?? {}) as Record<string, unknown>)}`),
  entityMeta: (set: string) => http<EntityMeta>('GET', `/meta/entities/${encodeURIComponent(set)}`),
  screens: () => http<ScreenSummary[]>('GET', '/screens'),
  screen: (id: string) => http<ScreenDef>('GET', `/screens/${encodeURIComponent(id)}`),
  saveScreen: (def: ScreenDef) => http<ScreenDef>('PUT', `/screens/${encodeURIComponent(def.id)}`, def),

  /** Every row matching the list's filters, for a CSV export (server-side paging, capped). */
  exportRows: <T = Rec>(set: string, p: ListParams & { max?: number }) => http<{ value: T[]; truncated: boolean; max: number }>('GET', `/export/${encodeURIComponent(set)}${qs(p as Record<string, unknown>)}`),

  // attachments (stored in SAP: the same files an SAP client user sees on the record)
  attachments: (set: string, key: string) => http<AttachmentList>('GET', `/attachments/${encodeURIComponent(set)}/${encodeURIComponent(key)}`),
  uploadAttachment: async (set: string, key: string, blob: Blob, filename: string, type: string) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${apiBase()}/api/attachments/${encodeURIComponent(set)}/${encodeURIComponent(key)}?filename=${encodeURIComponent(filename)}`, {
      method: 'POST', headers: { 'Content-Type': type || 'application/octet-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: blob,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(res.status, data?.message ?? data?.error ?? res.statusText, data?.sapCode ?? data?.code);
    return data as AttachmentList;
  },
  attachmentFile: async (set: string, key: string, line: number) => {
    const token = useAuth.getState().token;
    const res = await fetch(`${apiBase()}/api/attachments/${encodeURIComponent(set)}/${encodeURIComponent(key)}/file/${line}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new ApiError(res.status, 'Could not load attachment');
    return res.blob();
  },

  // generic data
  list: <T = Rec>(set: string, p: ListParams) => http<ODataList<T>>('GET', `/data/${encodeURIComponent(set)}${qs(p as Record<string, unknown>)}`),
  get: <T = Rec>(set: string, key: string | number, p: { select?: string; expand?: string } = {}) => http<T>('GET', `/data/${encodeURIComponent(set)}/${encodeURIComponent(String(key))}${qs(p)}`),
  create: <T = Rec>(set: string, body: Rec) => http<T>('POST', `/data/${encodeURIComponent(set)}`, body),
  update: (set: string, key: string | number, body: Rec, replaceCollections = false) => http<void>('PATCH', `/data/${encodeURIComponent(set)}/${encodeURIComponent(String(key))}${qs({ replaceCollections })}`, body),
  remove: (set: string, key: string | number) => http<void>('DELETE', `/data/${encodeURIComponent(set)}/${encodeURIComponent(String(key))}`),
  action: (set: string, key: string | number, name: string, body?: Rec) => http<Rec>('POST', `/data/${encodeURIComponent(set)}/${encodeURIComponent(String(key))}/actions/${name}`, body),

  saveScreenMeta: (id: string, patch: { title?: string; icon?: string; roles?: string[]; channels?: ('web'|'desktop'|'mobile')[]; menu?: { group?: string; subgroup?: string; order?: number; showOnHome?: boolean } }) => http<ScreenDef>('PATCH', `/screens/${encodeURIComponent(id)}/meta`, patch),
  reorderScreens: (items: { id: string; group: string; order: number; subgroup?: string }[]) => http<{ updated: number }>('PATCH', '/screens/reorder', items),

  // --- sector / module packs (per company) ---
  adminPacks: (db?: string) => http<{ companyDb: string; packs: Pack[] }>('GET', `/admin/packs${db ? `?db=${encodeURIComponent(db)}` : ''}`),
  setPack: (id: string, enabled: boolean, db?: string) => http<{ id: string; enabled: boolean }>('PATCH', `/admin/packs/${encodeURIComponent(id)}${db ? `?db=${encodeURIComponent(db)}` : ''}`, { enabled }),
  postingConfig: () => http<{ companyDb: string; fields: PostingField[] }>('GET', '/admin/posting-config'),
  setPostingConfig: (key: string, value: string) => http<{ key: string; value: string }>('PATCH', `/admin/posting-config/${encodeURIComponent(key)}`, { value }),
  deleteScreen: (id: string) => http<void>('DELETE', `/screens/${encodeURIComponent(id)}`),

  // admin: users, roles, permission catalog (all stored in SAP UDTs)
  adminUsers: () => http<AppUserRow[]>('GET', '/admin/users'),
  createUser: (b: Record<string, unknown>) => http<AppUserRow>('POST', '/admin/users', b),
  updateUser: (code: string, b: Record<string, unknown>) => http<void>('PATCH', `/admin/users/${encodeURIComponent(code)}`, b),
  deactivateUser: (code: string) => http<void>('DELETE', `/admin/users/${encodeURIComponent(code)}`),
  // --- tenants (multi-company, superuser) ---
  adminTenants: () => http<{ tenants: Tenant[]; controlDb: string; sldConfigured: boolean }>('GET', '/admin/tenants'),
  discoverTenants: () => http<{ sldConfigured: boolean; error?: string; companies: DiscoveredCompany[] }>('GET', '/admin/tenants/discover'),
  provisionTenant: (b: { companyDb: string; name?: string; adminPassword?: string }) => http<{ tenant: Tenant; summary: ProvisionSummary }>('POST', '/admin/tenants', b),
  updateTenant: (db: string, b: { active?: boolean; name?: string }) => http<Tenant>('PATCH', `/admin/tenants/${encodeURIComponent(db)}`, b),
  removeTenant: (db: string) => http<{ ok: boolean }>('DELETE', `/admin/tenants/${encodeURIComponent(db)}`),

  adminRoles: () => http<AppRoleRow[]>('GET', '/admin/roles'),
  createRole: (b: Record<string, unknown>) => http<AppRoleRow>('POST', '/admin/roles', b),
  updateRole: (code: string, b: Record<string, unknown>) => http<void>('PUT', `/admin/roles/${encodeURIComponent(code)}`, b),
  deleteRole: (code: string) => http<void>('DELETE', `/admin/roles/${encodeURIComponent(code)}`),
  // screen access: which screens each user / role / SAP user may see (control DB, per company)
  screenAccess: () => http<ScreenAccessData>('GET', '/admin/screen-access'),
  setScreenAccess: (kind: AccessKind, code: string, grants: Record<string, AccessLevel>) => http<AccessGrantRow>('PUT', `/admin/screen-access/${kind}/${encodeURIComponent(code)}`, { grants }),
  clearScreenAccess: (kind: AccessKind, code: string) => http<{ ok: boolean }>('DELETE', `/admin/screen-access/${kind}/${encodeURIComponent(code)}`),
  permissionCatalog: () => http<PermissionCatalog>('GET', '/admin/permission-catalog'),
  adminKpis: () => http<KpiRow[]>('GET', '/admin/kpis'),
  createKpi: (b: Record<string, unknown>) => http<KpiRow>('POST', '/admin/kpis', b),
  updateKpi: (code: string, b: Record<string, unknown>) => http<void>('PUT', `/admin/kpis/${encodeURIComponent(code)}`, b),
  deleteKpi: (code: string) => http<void>('DELETE', `/admin/kpis/${encodeURIComponent(code)}`),
  seedKpis: () => http<{ seeded: string[] }>('POST', '/admin/kpis/seed'),

  // document conversions (base-document linking; SAP copies prices & closes source lines)
  convert: (conversion: string, docEntry: number) => http<{ DocNum: number; DocTotal: number; DocCurrency?: string; target: string }>('POST', `/documents/${conversion}/${docEntry}`),
  certifyClaim: (code: string) => http<{ invoiceDocNum: number; revenue: number; invoiceTotal: number; retention: number; retentionPosted: boolean; netDueNow: number }>('POST', `/construction/claims/${encodeURIComponent(code)}/certify`),
  postMillRun: (code: string) => http<{ issueDocNum: number; receiptDocNum: number; ffbValue: number; cpo: { t: number; unit: number; cost: number }; pk: { t: number; unit: number; cost: number } }>('POST', `/oilpalm/millrun/${encodeURIComponent(code)}/post`),
  postRefineryRun: (code: string) => http<{ issueDocNum: number; receiptDocNum: number; cpoValue: number; outputs: { item: string; t: number; unit: number; cost: number }[] }>('POST', `/oilpalm/refinery/${encodeURIComponent(code)}/post`),
  postClaimAP: (code: string) => http<{ claim: string; vendor: string; apDocNum: number; amount: number; expenseAccount: string }>('POST', `/claims/${encodeURIComponent(code)}/post`),
  payClaim: (code: string) => http<{ claim: string; vendor: string; paymentDocNum: number; amount: number; bankAccount: string }>('POST', `/claims/${encodeURIComponent(code)}/pay`),
  postBatch: (code: string) => http<{ issueDocNum: number; receiptDocNum: number; ingredientValue: number; conversionValue: number; conversionJE?: number; bulkItem: string; bulkKg: number; bulkUnit: number }>('POST', `/fnb/batches/${encodeURIComponent(code)}/post`),
  releaseBatch: (code: string, reason?: string) => http<{ code: string; status: string; releasedBy: string; reason: string; qcTests: number; deviationsChecked: number }>('POST', `/fnb/batches/${encodeURIComponent(code)}/release`, { reason }),
  rejectBatch: (code: string, reason: string) => http<{ code: string; status: string; rejectedBy: string; reason: string }>('POST', `/fnb/batches/${encodeURIComponent(code)}/reject`, { reason }),
  packOut: (code: string) => http<{ issueDocNum: number; receiptDocNum: number; fgItem: string; units: number; fgUnit: number; udi: string }>('POST', `/fnb/pack/${encodeURIComponent(code)}/post`),
  explodeRecipe: (code: string) => http<{ batch: string; bulkItem: string; bulkKg: number; batchSize: number; yieldPct: number; lines: number }>('POST', `/fnb/recipes/${encodeURIComponent(code)}/explode`),

  // operational budgeting (budget vs actual is served from an incrementally-synced cache of SAP journals)
  budgetVariance: (p: { version: string; groupBy?: string; fromPeriod?: number; toPeriod?: number; sync?: boolean }) =>
    http<BudgetVariance>('GET', `/budget/variance${qs(p as Record<string, unknown>)}`),
  syncBudgetActuals: () => http<{ scanned: number; folded: number; lastJdt: number; more: boolean }>('POST', '/budget/actuals/sync'),
  rebuildBudgetActuals: () => http<{ rebuilt: boolean; scanned: number; folded: number; lastJdt: number; more: boolean }>('POST', '/budget/actuals/rebuild'),
  budgetActualsStatus: () => http<{ lastJdt: number; lastRun?: string; rowsSeen: number; note?: string }>('GET', '/budget/actuals/status'),
  submitBudgetVersion: (code: string) => http<{ code: string; name?: string; from: string; status: string }>('POST', `/budget/versions/${encodeURIComponent(code)}/submit`),
  approveBudgetVersion: (code: string) => http<{ code: string; name?: string; from: string; status: string }>('POST', `/budget/versions/${encodeURIComponent(code)}/approve`),
  rejectBudgetVersion: (code: string) => http<{ code: string; name?: string; from: string; status: string }>('POST', `/budget/versions/${encodeURIComponent(code)}/reject`),
  copyBudgetVersion: (code: string, body: { newCode: string; newName?: string; year?: string; upliftPct?: number }) =>
    http<{ code: string; name: string; copied: number; failed: number; upliftPct: number }>('POST', `/budget/versions/${encodeURIComponent(code)}/copy`, body),
  spreadBudgetLine: (code: string, body: { profile?: string; total?: number }) =>
    http<{ code: string; profile: string; total: number; periods: number[] }>('POST', `/budget/lines/${encodeURIComponent(code)}/spread`, body),

  // financial consolidation (the group lives in the control DB, independent of SAP)
  consGroups: () => http<(ConsGroup & { members: number })[]>('GET', '/consolidation/groups'),
  consGroup: (id: string) => http<ConsGroupDetail>('GET', `/consolidation/groups/${encodeURIComponent(id)}`),
  saveConsGroup: (id: string, b: { name: string; currency?: string; ctaAccount?: string; note?: string }) =>
    http<ConsGroup>('PUT', `/consolidation/groups/${encodeURIComponent(id)}`, b),
  deleteConsGroup: (id: string) => http<{ deleted: string }>('DELETE', `/consolidation/groups/${encodeURIComponent(id)}`),
  saveConsMember: (id: string, m: ConsMember) =>
    http<{ saved: ConsMember; warnings: string[] }>('PUT', `/consolidation/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(m.memberId)}`, m),
  deleteConsMember: (id: string, memberId: string) =>
    http<{ deleted: string }>('DELETE', `/consolidation/groups/${encodeURIComponent(id)}/members/${encodeURIComponent(memberId)}`),
  saveConsAccounts: (id: string, rows: ConsAccount[]) =>
    http<{ saved: number }>('PUT', `/consolidation/groups/${encodeURIComponent(id)}/accounts`, rows),
  consSourceAccounts: (id: string, db: string) =>
    http<ConsSourceAccounts>('GET', `/consolidation/groups/${encodeURIComponent(id)}/source-accounts${qs({ db })}`),
  saveConsMap: (id: string, rows: { source: string; localAccount: string; groupAccount: string }[]) =>
    http<{ saved: number }>('PUT', `/consolidation/groups/${encodeURIComponent(id)}/map`, rows),
  consJournals: (id: string, year?: string) =>
    http<ConsJournal[]>('GET', `/consolidation/groups/${encodeURIComponent(id)}/journals${qs({ year })}`),
  saveConsJournal: (id: string, jid: string, j: Omit<ConsJournal, 'id'>) =>
    http<{ saved: string; lines: number }>('PUT', `/consolidation/groups/${encodeURIComponent(id)}/journals/${encodeURIComponent(jid)}`, j),
  deleteConsJournal: (id: string, jid: string) =>
    http<{ deleted: string }>('DELETE', `/consolidation/groups/${encodeURIComponent(id)}/journals/${encodeURIComponent(jid)}`),
  consSync: (id: string) => http<ConsSyncResult>('POST', `/consolidation/groups/${encodeURIComponent(id)}/sync`),
  consTrialBalance: (id: string, p: { year: string; fromPeriod?: number; toPeriod?: number; includeJournals?: boolean }) =>
    http<ConsTrialBalance>('GET', `/consolidation/groups/${encodeURIComponent(id)}/trial-balance${qs(p as Record<string, unknown>)}`),

  // payment creation (specialised allocation workflow)
  openInvoices: (direction: string, cardCode: string) => http<OpenInvoice[]>('GET', `/payments/${direction}/open-invoices${qs({ cardCode })}`),
  createPayment: (direction: string, body: { cardCode: string; docDate?: string; transferAccount: string; invoices: { docEntry: number; sumApplied: number }[] }) =>
    http<{ DocNum: number; DocEntry: number; total: number; currency?: string }>('POST', `/payments/${direction}`, body),
};

/** Named code actions attachable to screens via ActionDef{kind:'code'}. Return a message to show. */
export const CODE_ACTION_LIST: { name: string; label: string; entity?: string }[] = [
  { name: 'copyToInvoice', label: 'Copy to Invoice', entity: 'Orders' },
  { name: 'quoteToOrder', label: 'Copy to Order', entity: 'Quotations' },
  { name: 'orderToDelivery', label: 'Create Delivery', entity: 'Orders' },
  { name: 'deliveryToInvoice', label: 'Copy to Invoice', entity: 'DeliveryNotes' },
  { name: 'poToGrpo', label: 'Copy to Goods Receipt', entity: 'PurchaseOrders' },
  { name: 'grpoToApInvoice', label: 'Copy to A/P Invoice', entity: 'PurchaseDeliveryNotes' },
  // Budgeting. Listed here as well as in CODE_ACTIONS so they are selectable in the Designer — an action
  // present only in CODE_ACTIONS executes fine but cannot be attached to a screen through the UI.
  { name: 'submitBudget', label: 'Submit for approval', entity: 'U_BG_VERSION' },
  { name: 'approveBudget', label: 'Approve', entity: 'U_BG_VERSION' },
  { name: 'rejectBudget', label: 'Reject', entity: 'U_BG_VERSION' },
  { name: 'copyBudgetVersion', label: 'Copy to new version', entity: 'U_BG_VERSION' },
  { name: 'spreadBudgetLine', label: 'Spread annual total', entity: 'U_BG_LINE' },
];

export const CODE_ACTIONS: Record<string, (rec: Rec) => Promise<string>> = {
  copyToInvoice: async (rec) => { const r = await api.convert('order-to-invoice', Number(rec.DocEntry)); return `A/R Invoice ${r.DocNum} created (total ${r.DocTotal} ${r.DocCurrency ?? ''})`; },
  quoteToOrder: async (rec) => { const r = await api.convert('quote-to-order', Number(rec.DocEntry)); return `Sales Order ${r.DocNum} created (total ${r.DocTotal} ${r.DocCurrency ?? ''})`; },
  orderToDelivery: async (rec) => { const r = await api.convert('order-to-delivery', Number(rec.DocEntry)); return `Delivery ${r.DocNum} created`; },
  deliveryToInvoice: async (rec) => { const r = await api.convert('delivery-to-invoice', Number(rec.DocEntry)); return `A/R Invoice ${r.DocNum} created (total ${r.DocTotal} ${r.DocCurrency ?? ''})`; },
  poToGrpo: async (rec) => { const r = await api.convert('po-to-grpo', Number(rec.DocEntry)); return `Goods Receipt PO ${r.DocNum} created`; },
  grpoToApInvoice: async (rec) => { const r = await api.convert('grpo-to-apinvoice', Number(rec.DocEntry)); return `A/P Invoice ${r.DocNum} created (total ${r.DocTotal} ${r.DocCurrency ?? ''})`; },
  certifyProgressClaim: async (rec) => {
    const r = await api.certifyClaim(String(rec.Code));
    const ret = r.retentionPosted ? `, retention ${r.retention.toLocaleString()} held` : '';
    return `A/R Invoice ${r.invoiceDocNum} posted — revenue ${r.revenue.toLocaleString()}${ret}. Net due now ${r.netDueNow.toLocaleString()}.`;
  },
  postMillRun: async (rec) => {
    const r = await api.postMillRun(String(rec.Code));
    return `Posted — FFB issued (GI ${r.issueDocNum}) value ${r.ffbValue.toLocaleString()}; received CPO ${r.cpo.t}t @ ${r.cpo.unit.toLocaleString()} + PK ${r.pk.t}t @ ${r.pk.unit.toLocaleString()} (GR ${r.receiptDocNum}).`;
  },
  postRefineryRun: async (rec) => {
    const r = await api.postRefineryRun(String(rec.Code));
    const outs = r.outputs.map(o => `${o.item} ${o.t}t @ ${o.unit.toLocaleString()}`).join(', ');
    return `Posted — CPO issued (GI ${r.issueDocNum}) value ${r.cpoValue.toLocaleString()}; received ${outs} (GR ${r.receiptDocNum}).`;
  },
  postClaimAP: async (rec) => {
    const r = await api.postClaimAP(String(rec.Code));
    return `A/P journal ${r.apDocNum} raised against vendor ${r.vendor} for ${r.amount.toLocaleString()} (Dr ${r.expenseAccount}, Cr vendor A/P). Claim ${r.claim} is now a payable — use "Pay claim" to settle it.`;
  },
  payClaim: async (rec) => {
    const r = await api.payClaim(String(rec.Code));
    return `Paid ${r.amount.toLocaleString()} to vendor ${r.vendor} — outgoing payment ${r.paymentDocNum} from ${r.bankAccount}. Claim ${r.claim} settled.`;
  },
  postBatch: async (rec) => {
    const r = await api.postBatch(String(rec.Code));
    const conv = r.conversionValue > 0 ? ` + conversion ${r.conversionValue.toLocaleString()} (JE ${r.conversionJE})` : '';
    return `Batch made — ingredients issued (GI ${r.issueDocNum}) value ${r.ingredientValue.toLocaleString()}${conv}; received ${r.bulkKg} kg ${r.bulkItem} @ ${r.bulkUnit.toLocaleString()}/kg (GR ${r.receiptDocNum}).`;
  },
  releaseBatch: async (rec) => {
    const reason = window.prompt('QA disposition — reason for release (meaning of signature):', 'All results within specification; approved for distribution');
    if (reason === null) return 'Release cancelled.';
    const r = await api.releaseBatch(String(rec.Code), reason);
    return `Batch ${r.code} QA-released by ${r.releasedBy} — "${r.reason}". ${r.qcTests} QC test(s) passed; ${r.deviationsChecked} deviation(s) checked. It can now be packed.`;
  },
  rejectBatch: async (rec) => {
    const reason = window.prompt('QA disposition — reason for REJECTION (required):', '');
    if (reason === null) return 'Rejection cancelled.';
    if (!reason.trim()) throw new Error('A rejection reason is required (GMP meaning of signature).');
    const r = await api.rejectBatch(String(rec.Code), reason);
    return `Batch ${r.code} QA-REJECTED by ${r.rejectedBy} — "${r.reason}". It cannot be packed.`;
  },
  createBatchFromRecipe: async (rec) => {
    const r = await api.explodeRecipe(String(rec.Code));
    return `Batch ${r.batch} created from recipe ${rec.Code} — ${r.bulkKg} kg ${r.bulkItem} (${r.lines} ingredient line(s) scaled to ${r.batchSize} kg). Open Batches to post it.`;
  },
  packOut: async (rec) => {
    const r = await api.packOut(String(rec.Code));
    return `Packed — issued bulk + packaging (GI ${r.issueDocNum}); received ${r.units} × ${r.fgItem} @ ${r.fgUnit.toLocaleString()}/unit (GR ${r.receiptDocNum}). UDI ${r.udi}.`;
  },
  submitBudget: async (rec) => {
    const r = await api.submitBudgetVersion(String(rec.Code));
    return `Budget ${r.code} submitted for approval. Someone else must approve it.`;
  },
  approveBudget: async (rec) => {
    const r = await api.approveBudgetVersion(String(rec.Code));
    return `Budget ${r.code} approved and locked. Budget vs actual now reports against it.`;
  },
  rejectBudget: async (rec) => {
    const r = await api.rejectBudgetVersion(String(rec.Code));
    return `Budget ${r.code} rejected and returned to the owner for revision.`;
  },
  copyBudgetVersion: async (rec) => {
    const newCode = window.prompt('New budget version code:', `${String(rec.Code)}-V2`);
    if (newCode === null) return 'Copy cancelled.';
    if (!newCode.trim()) throw new Error('A version code is required.');
    const uplift = window.prompt('Uplift every amount by what % (0 for a straight copy)?', '0');
    if (uplift === null) return 'Copy cancelled.';
    const upliftPct = Number(uplift);
    if (!Number.isFinite(upliftPct)) throw new Error('The uplift must be a number.');
    const r = await api.copyBudgetVersion(String(rec.Code), { newCode: newCode.trim(), upliftPct });
    const failed = r.failed ? ` ${r.failed} line(s) FAILED to copy.` : '';
    return `Budget ${r.code} created from ${String(rec.Code)} — ${r.copied} line(s) copied${r.upliftPct ? ` with ${r.upliftPct}% uplift` : ''}.${failed}`;
  },
  spreadBudgetLine: async (rec) => {
    const profile = window.prompt('Spread profile — even, rising, or days (calendar-weighted):', 'even');
    if (profile === null) return 'Spread cancelled.';
    const p = profile.trim().toLowerCase();
    if (!['even', 'rising', 'days'].includes(p)) throw new Error('Profile must be even, rising or days.');
    const r = await api.spreadBudgetLine(String(rec.Code), { profile: p });
    return `Spread ${r.total.toLocaleString()} across 12 periods (${r.profile}): ${r.periods.map(n => n.toLocaleString()).join(', ')}.`;
  },
};
